import { useState, useRef, useEffect } from "react";
import { Edit, Trash2, Eye, Plus, Loader2, Search, ChevronDown, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import tarrantCountyZipCodes from "../../data/tarrant-county-zip-codes.json";
import { INCOME_RANGES } from "../../constants/resource-constants";

export default function ResourcesTable({ data = [], onDelete, isLoading, errorMessage }) {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [zipFilter, setZipFilter] = useState("");
  const [zipSearch, setZipSearch] = useState("");
  const [isZipDropdownOpen, setIsZipDropdownOpen] = useState(false);
  const zipDropdownRef = useRef(null);

  const [incomeFilter, setIncomeFilter] = useState("");
  const [isIncomeDropdownOpen, setIsIncomeDropdownOpen] = useState(false);
  const incomeDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (zipDropdownRef.current && !zipDropdownRef.current.contains(e.target))
        setIsZipDropdownOpen(false);
      if (incomeDropdownRef.current && !incomeDropdownRef.current.contains(e.target))
        setIsIncomeDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredZips = tarrantCountyZipCodes.filter((z) => z.startsWith(zipSearch));

  const filteredData = data.filter((r) => {
    const matchesSearch =
      !search || r.title?.toLowerCase().includes(search.toLowerCase());
    const matchesZip =
      !zipFilter || r.zipCode === zipFilter;
    const matchesIncome =
      !incomeFilter || r.incomeRange === incomeFilter;
    return matchesSearch && matchesZip && matchesIncome;
  });

  const hasActiveFilters = search || zipFilter || incomeFilter;

  const clearFilters = () => {
    setSearch("");
    setZipFilter("");
    setZipSearch("");
    setIncomeFilter("");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader2 className="animate-spin text-[#3FC4E3]" size={32} />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="py-8 text-center text-red-500 font-popinsRegular text-sm">{errorMessage}</div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-popinsBold text-xl lg:text-2xl">Manage Resources</h1>
          <p className="font-popinsRegular text-sm text-black/50 mt-1">
            Create and manage resources offered by your organization.
          </p>
        </div>
        <button
          onClick={() => navigate("/dashboard/manage-resources/new")}
          className="flex items-center gap-2 bg-[#3FC4E3] text-white px-4 py-2 rounded-md font-popinsMedium text-sm hover:bg-[#006077] transition-colors"
        >
          <Plus size={16} />
          Add Resource
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {/* Text search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title…"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-border font-popinsMedium text-sm text-black/60 outline-none focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] transition-all"
          />
        </div>

        {/* ZIP Code filter */}
        <div className="relative" ref={zipDropdownRef}>
          <button
            type="button"
            onClick={() => setIsZipDropdownOpen(!isZipDropdownOpen)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border font-popinsMedium text-sm transition-all outline-none min-w-[140px] ${
              zipFilter
                ? "border-[#3FC4E3] text-[#3FC4E3] bg-[#3FC4E3]/5"
                : "border-border text-black/50 bg-white"
            }`}
          >
            <span className="flex-1 text-left truncate">{zipFilter || "ZIP Code"}</span>
            {zipFilter ? (
              <X
                size={14}
                className="flex-shrink-0 hover:text-black/70"
                onClick={(e) => {
                  e.stopPropagation();
                  setZipFilter("");
                  setZipSearch("");
                  setIsZipDropdownOpen(false);
                }}
              />
            ) : (
              <ChevronDown size={15} className={`flex-shrink-0 transition-transform ${isZipDropdownOpen ? "rotate-180" : ""}`} />
            )}
          </button>

          {isZipDropdownOpen && (
            <div className="absolute z-50 left-0 mt-1 w-48 bg-white border border-border rounded-md shadow-lg">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <Search size={13} className="text-black/30 flex-shrink-0" />
                <input
                  type="text"
                  autoFocus
                  value={zipSearch}
                  onChange={(e) => setZipSearch(e.target.value.replace(/\D/g, ""))}
                  placeholder="Search ZIP…"
                  className="w-full text-sm font-popinsMedium text-black/60 outline-none bg-transparent"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredZips.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-black/40 font-popinsMedium">No match</div>
                ) : (
                  filteredZips.map((z) => (
                    <div
                      key={z}
                      onClick={() => {
                        setZipFilter(z);
                        setZipSearch(z);
                        setIsZipDropdownOpen(false);
                      }}
                      className={`px-4 py-2 text-sm font-popinsMedium cursor-pointer hover:bg-[#3FC4E3]/10 ${
                        zipFilter === z ? "text-[#3FC4E3]" : "text-black/60"
                      }`}
                    >
                      {z}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Income Range filter */}
        <div className="relative" ref={incomeDropdownRef}>
          <button
            type="button"
            onClick={() => setIsIncomeDropdownOpen(!isIncomeDropdownOpen)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border font-popinsMedium text-sm transition-all outline-none min-w-[160px] ${
              incomeFilter
                ? "border-[#3FC4E3] text-[#3FC4E3] bg-[#3FC4E3]/5"
                : "border-border text-black/50 bg-white"
            }`}
          >
            <span className="flex-1 text-left truncate">{incomeFilter || "Income Range"}</span>
            {incomeFilter ? (
              <X
                size={14}
                className="flex-shrink-0 hover:text-black/70"
                onClick={(e) => {
                  e.stopPropagation();
                  setIncomeFilter("");
                  setIsIncomeDropdownOpen(false);
                }}
              />
            ) : (
              <ChevronDown size={15} className={`flex-shrink-0 transition-transform ${isIncomeDropdownOpen ? "rotate-180" : ""}`} />
            )}
          </button>

          {isIncomeDropdownOpen && (
            <div className="absolute z-50 left-0 mt-1 w-44 bg-white border border-border rounded-md shadow-lg">
              <div className="max-h-52 overflow-y-auto py-1">
                {INCOME_RANGES.map((range) => (
                  <div
                    key={range}
                    onClick={() => {
                      setIncomeFilter(range);
                      setIsIncomeDropdownOpen(false);
                    }}
                    className={`px-4 py-2 text-sm font-popinsMedium cursor-pointer hover:bg-[#3FC4E3]/10 ${
                      incomeFilter === range ? "text-[#3FC4E3]" : "text-black/60"
                    }`}
                  >
                    {range}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm font-popinsMedium text-black/40 hover:text-black/70 transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      {filteredData.length === 0 ? (
        <div className="py-16 text-center text-black/40 font-popinsRegular text-sm">
          {hasActiveFilters
            ? "No resources match the selected filters."
            : 'No resources yet. Click "Add Resource" to get started.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm font-popinsRegular">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-popinsSemiBold text-black/70">Title</th>
                <th className="text-left px-4 py-3 font-popinsSemiBold text-black/70">Categories</th>
                <th className="text-left px-4 py-3 font-popinsSemiBold text-black/70">Income Range</th>
                <th className="text-left px-4 py-3 font-popinsSemiBold text-black/70">ZIP Code</th>
                <th className="text-left px-4 py-3 font-popinsSemiBold text-black/70">Location</th>
                <th className="text-right px-4 py-3 font-popinsSemiBold text-black/70">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((resource) => (
                <tr key={resource.id} className="border-b border-border last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-popinsMedium text-black/80 max-w-[200px] truncate">
                    {resource.title}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {resource.categories?.length > 0 ? (
                        resource.categories.map((cat) => (
                          <span
                            key={cat.id}
                            className="px-2 py-0.5 bg-[#3FC4E3]/10 text-[#3FC4E3] rounded text-xs font-popinsMedium"
                          >
                            {cat.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-black/30 text-xs">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-black/60">
                    {resource.incomeRange || "—"}
                  </td>
                  <td className="px-4 py-3 text-black/60">
                    {resource.zipCode || "—"}
                  </td>
                  <td className="px-4 py-3 text-black/60 max-w-[160px] truncate">
                    {resource.location || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() =>
                          navigate(`/dashboard/manage-resources/${resource.id}`, { state: { resource } })
                        }
                        className="p-1.5 rounded hover:bg-[#3FC4E3]/10 text-[#3FC4E3] transition-colors"
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() =>
                          navigate(`/dashboard/manage-resources/${resource.id}/edit`, { state: { resource } })
                        }
                        className="p-1.5 rounded hover:bg-gray-100 text-black/50 transition-colors"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(resource)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
